import { type ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "~/components/ui/button";
import Modal from "~/components/ui/modal";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ProjectApi } from "~/functions/api/project";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import type { Project, UpdateProject } from "~/interface/project";
import { setCurrentProject } from "~/slices/auth";
import type { RootState } from "~/store";
import { dateToISOString } from "~/utils/customDate";
import ProjectModalContent from "../projectModalContent";
import styles from "./index.module.scss";

interface ProjectInfo {
  property: string;
  description: string | undefined;
}

interface ProjectTopListContentProps {
  data?: object[];
  columns?: ColumnDef<object>[];
  updateData?: (rowIndex: number, columnId: string, value: string | number) => void;
  tabTitle?: string;
  onUserRemoved?: (userId: string) => void;
  onRoleChanged?: (userId: string, newRole: string) => void;
  onMembersAdded?: (addedMembers: any[]) => void;
}

const ProjectTopListContent = (_props: ProjectTopListContentProps) => {
  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);
  const { canEditProject } = useProjectPermission();

  const projectTableData: ProjectInfo[] = useMemo(() => {
    if (!currentProject) return [];
    return [
      { property: "プロジェクト名", description: currentProject.name ?? "―" },
      { property: "説明", description: currentProject.description ?? "―" },
      {
        property: "契約期間",
        description: `${
          typeof currentProject.start_date === "string"
            ? new Date(currentProject.start_date).toISOString().split("T")[0]
            : "指定なし"
        } ～ ${
          typeof currentProject.end_date === "string"
            ? new Date(currentProject.end_date).toISOString().split("T")[0]
            : "指定なし"
        }`,
      },
      { property: "クライアント名", description: currentProject.client?.name ?? "―" },
      { property: "クライアントURL", description: currentProject.client?.url ?? "―" },
    ];
  }, [currentProject]);

  const [projectData, setProjectData] = useState<UpdateProject>({
    name: currentProject?.name,
    description: currentProject?.description,
    start_date: currentProject?.start_date
      ? dateToISOString(new Date(currentProject.start_date))
      : "",
    end_date: currentProject?.end_date ? dateToISOString(new Date(currentProject.end_date)) : "",
    client: {
      name: currentProject?.client?.name,
      url: currentProject?.client?.url,
    },
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isEditProjectModalOpen, setEditProjectModalOpen] = useState(false);
  const [isLock, setIsLock] = useState(false);

  const dispatch = useDispatch();

  const columns: ColumnDef<object>[] = [
    {
      header: "項目名",
      accessorKey: "property",
    },
    {
      header: "値",
      accessorKey: "description",
    },
  ];
  const list = useReactTable({
    data: projectTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    // 必須項目がすべて入力されているかをチェックし、boolean 型に変換
    const isFormFilled = Boolean(projectData.name);
    setIsFormValid(isFormFilled);
  }, [projectData]);

  // プロジェクト編集モーダルを閉じる関数
  const closeEditProjectModal = () => setEditProjectModalOpen(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Check if the field is a date field and convert accordingly
    if (name === "start_date" || name === "end_date") {
      setProjectData((prevData) => ({
        ...prevData,
        [name]: value ? dateToISOString(new Date(value)) : "",
      }));
    } else if (name === "client.name") {
      setProjectData((prevData) => ({
        ...prevData,
        client: {
          name: value,
          url: prevData.client?.url,
        },
      }));
    } else if (name === "client.url") {
      setProjectData((prevData) => ({
        ...prevData,
        client: {
          name: prevData.client?.name,
          url: value,
        },
      }));
    } else {
      setProjectData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  // プロジェクトデータの更新処理
  const handleUpdateProject = async () => {
    setIsLock(true);
    if (currentProject.id === undefined || currentUser?.organization_id === undefined) {
      toast.error(TOAST_MESSAGES.PROJECT.INVALID_INPUT_FORMAT);
      return;
    }
    const preparedProjectData: UpdateProject = {
      ...projectData,
      description: projectData.description !== "" ? projectData.description : undefined,
      start_date: projectData.start_date !== "" ? projectData.start_date : undefined,
      end_date: projectData.end_date !== "" ? projectData.end_date : undefined,
      client: {
        name: projectData.client.name !== "" ? projectData.client.name : undefined,
        url: projectData.client.url !== "" ? projectData.client.url : undefined,
      },
    };

    const projectApi = new ProjectApi();

    const response = await projectApi.update(
      currentUser.organization_id,
      currentProject.id,
      preparedProjectData,
    );

    if (response !== null) {
      const data: Project = await response.json();
      dispatch(setCurrentProject(data));
      toast.success(TOAST_MESSAGES.PROJECT.UPDATE_SUCCESS);
      closeEditProjectModal(); // モーダルを閉じる
    } else {
      toast.error(TOAST_MESSAGES.PROJECT.UPDATE_FAILED);
    }
    setIsLock(false);
  };

  return (
    <div className={styles["ProjectTopListContent"]}>
      <div className={styles["title"]}>
        <h3>詳細情報</h3>
        <Button
          label="編集"
          styleType="blue"
          onClick={() => setEditProjectModalOpen(true)}
          disabled={!canEditProject()}
        />
      </div>
      <ul className={styles["ul"]}>
        {list.getRowModel().rows.map((row) => (
          <li className={styles["li"]} key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <div key={cell.id}>
                <span>{cell.getValue() as string}</span>
              </div>
            ))}
          </li>
        ))}
      </ul>
      <Modal
        label={`詳細情報の編集`}
        isOpen={isEditProjectModalOpen}
        onClose={() => setEditProjectModalOpen(false)}
        content={
          <ProjectModalContent
            projectData={projectData}
            handleInputChange={handleInputChange}
            handleUpdateProject={handleUpdateProject}
            closeModal={closeEditProjectModal}
            isFormValid={isFormValid}
            isLock={isLock}
            startDateString={projectData.start_date?.split("T")[0] || ""}
            endDateString={projectData.end_date?.split("T")[0] || ""}
            buttonLabel={!isLock ? "保存" : "保存中"}
            isEditMode={true}
          />
        }
        className="projectCreateModal"
      />
    </div>
  );
};

export default ProjectTopListContent;
